import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Logger } from '../../../core/services/logger.service';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

interface CropArea {
  x: number;
  y: number;
  size: number;
}

@Component({
  selector: 'app-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss'],
})
export class ImageCropperComponent implements OnInit {
  private readonly logger = new Logger(ImageCropperComponent.name);
  @ViewChild('canvas', { static: false })
  canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('imageElement', { static: false })
  imageElement!: ElementRef<HTMLImageElement>;

  public imageSource = '';
  public cropArea: CropArea = { x: 0, y: 0, size: 200 };
  public isDragging = false;
  public dragStart = { x: 0, y: 0 };
  public scale = 1;
  public imageLoaded = false;
  public imageError = false;

  private ctx!: CanvasRenderingContext2D | null;
  private image = new Image();

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    if (this.config.data?.imageSource) {
      this.imageSource = this.config.data.imageSource;
    }
  }

  ngOnInit(): void {
    this.loadImage();
  }

  private loadImage(): void {
    this.image.onload = () => {
      this.imageLoaded = true;
      this.initializeCropArea();
      setTimeout(() => this.drawCanvas(), 100);
    };

    this.image.onerror = () => {
      this.logger.error("Erreur lors du chargement de l'image", this.imageSource);
      this.imageLoaded = false;
      this.imageError = true;
    };

    this.image.crossOrigin = 'anonymous';
    this.image.src = this.imageSource;
  }

  private initializeCropArea(): void {
    const minDimension = Math.min(this.image.width, this.image.height);
    this.cropArea.size = minDimension * 0.8;
    this.cropArea.x = (this.image.width - this.cropArea.size) / 2;
    this.cropArea.y = (this.image.height - this.cropArea.size) / 2;

    // Calculate scale to fit image in container - responsive
    const viewportWidth = window.innerWidth;
    const containerWidth = viewportWidth < 768 ? viewportWidth - 64 : 600;
    const containerHeight = viewportWidth < 768 ? 250 : 400;
    const scaleX = containerWidth / this.image.width;
    const scaleY = containerHeight / this.image.height;
    this.scale = Math.min(scaleX, scaleY, 1);
  }

  private drawCanvas(): void {
    if (!this.canvas || !this.canvas.nativeElement) return;

    const canvas = this.canvas.nativeElement;
    this.ctx = canvas.getContext('2d');
    if (!this.ctx) return;

    // Set canvas size
    canvas.width = this.image.width * this.scale;
    canvas.height = this.image.height * this.scale;

    // Clear canvas
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the full image as background
    this.ctx.drawImage(this.image, 0, 0, canvas.width, canvas.height);

    // Calculate scaled crop area
    const scaledCropArea = {
      x: this.cropArea.x * this.scale,
      y: this.cropArea.y * this.scale,
      size: this.cropArea.size * this.scale,
    };

    // Draw semi-transparent overlay outside crop area
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';

    // Top
    this.ctx.fillRect(0, 0, canvas.width, scaledCropArea.y);
    // Bottom
    this.ctx.fillRect(
      0,
      scaledCropArea.y + scaledCropArea.size,
      canvas.width,
      canvas.height - (scaledCropArea.y + scaledCropArea.size)
    );
    // Left
    this.ctx.fillRect(
      0,
      scaledCropArea.y,
      scaledCropArea.x,
      scaledCropArea.size
    );
    // Right
    this.ctx.fillRect(
      scaledCropArea.x + scaledCropArea.size,
      scaledCropArea.y,
      canvas.width - (scaledCropArea.x + scaledCropArea.size),
      scaledCropArea.size
    );

    // Draw crop area border
    this.ctx.strokeStyle = '#3B82F6';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(
      scaledCropArea.x,
      scaledCropArea.y,
      scaledCropArea.size,
      scaledCropArea.size
    );

    // Draw corner handles
    this.drawCornerHandles(scaledCropArea);
  }

  private drawCornerHandles(cropArea: {
    x: number;
    y: number;
    size: number;
  }): void {
    if (!this.ctx) return;

    const handleSize = 20;
    const handleWidth = 3;

    this.ctx.strokeStyle = '#3B82F6';
    this.ctx.lineWidth = handleWidth;

    // Top-left
    this.ctx.beginPath();
    this.ctx.moveTo(cropArea.x, cropArea.y + handleSize);
    this.ctx.lineTo(cropArea.x, cropArea.y);
    this.ctx.lineTo(cropArea.x + handleSize, cropArea.y);
    this.ctx.stroke();

    // Top-right
    this.ctx.beginPath();
    this.ctx.moveTo(cropArea.x + cropArea.size - handleSize, cropArea.y);
    this.ctx.lineTo(cropArea.x + cropArea.size, cropArea.y);
    this.ctx.lineTo(cropArea.x + cropArea.size, cropArea.y + handleSize);
    this.ctx.stroke();

    // Bottom-left
    this.ctx.beginPath();
    this.ctx.moveTo(cropArea.x, cropArea.y + cropArea.size - handleSize);
    this.ctx.lineTo(cropArea.x, cropArea.y + cropArea.size);
    this.ctx.lineTo(cropArea.x + handleSize, cropArea.y + cropArea.size);
    this.ctx.stroke();

    // Bottom-right
    this.ctx.beginPath();
    this.ctx.moveTo(
      cropArea.x + cropArea.size - handleSize,
      cropArea.y + cropArea.size
    );
    this.ctx.lineTo(cropArea.x + cropArea.size, cropArea.y + cropArea.size);
    this.ctx.lineTo(
      cropArea.x + cropArea.size,
      cropArea.y + cropArea.size - handleSize
    );
    this.ctx.stroke();
  }

  onMouseDown(event: MouseEvent): void {
    const rect = this.canvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const scaledCropArea = {
      x: this.cropArea.x * this.scale,
      y: this.cropArea.y * this.scale,
      size: this.cropArea.size * this.scale,
    };

    // Check if click is inside crop area
    if (
      x >= scaledCropArea.x &&
      x <= scaledCropArea.x + scaledCropArea.size &&
      y >= scaledCropArea.y &&
      y <= scaledCropArea.y + scaledCropArea.size
    ) {
      this.isDragging = true;
      this.dragStart = { x: x - scaledCropArea.x, y: y - scaledCropArea.y };
    }
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    const rect = this.canvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Calculate new position
    let newX = (x - this.dragStart.x) / this.scale;
    let newY = (y - this.dragStart.y) / this.scale;

    // Constrain to image bounds
    newX = Math.max(0, Math.min(this.image.width - this.cropArea.size, newX));
    newY = Math.max(0, Math.min(this.image.height - this.cropArea.size, newY));

    this.cropArea.x = newX;
    this.cropArea.y = newY;

    this.drawCanvas();
  }

  onMouseUp(): void {
    this.isDragging = false;
  }

  onTouchStart(event: TouchEvent): void {
    event.preventDefault();
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const rect = this.canvas.nativeElement.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    const scaledCropArea = {
      x: this.cropArea.x * this.scale,
      y: this.cropArea.y * this.scale,
      size: this.cropArea.size * this.scale,
    };

    // Check if touch is inside crop area
    if (
      x >= scaledCropArea.x &&
      x <= scaledCropArea.x + scaledCropArea.size &&
      y >= scaledCropArea.y &&
      y <= scaledCropArea.y + scaledCropArea.size
    ) {
      this.isDragging = true;
      this.dragStart = { x: x - scaledCropArea.x, y: y - scaledCropArea.y };
    }
  }

  onTouchMove(event: TouchEvent): void {
    event.preventDefault();
    if (!this.isDragging || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const rect = this.canvas.nativeElement.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    // Calculate new position
    let newX = (x - this.dragStart.x) / this.scale;
    let newY = (y - this.dragStart.y) / this.scale;

    // Constrain to image bounds
    newX = Math.max(0, Math.min(this.image.width - this.cropArea.size, newX));
    newY = Math.max(0, Math.min(this.image.height - this.cropArea.size, newY));

    this.cropArea.x = newX;
    this.cropArea.y = newY;

    this.drawCanvas();
  }

  onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();

    const delta = event.deltaY > 0 ? -10 : 10;
    const newSize = this.cropArea.size + delta;

    // Constrain size
    const minSize = 50;
    const maxSize = Math.min(
      this.image.width - this.cropArea.x,
      this.image.height - this.cropArea.y
    );

    if (newSize >= minSize && newSize <= maxSize) {
      // Adjust position to keep crop centered
      const sizeDiff = newSize - this.cropArea.size;
      this.cropArea.x = Math.max(0, this.cropArea.x - sizeDiff / 2);
      this.cropArea.y = Math.max(0, this.cropArea.y - sizeDiff / 2);
      this.cropArea.size = newSize;

      this.drawCanvas();
    }
  }

  crop(): void {
    const cropCanvas = document.createElement('canvas');
    const cropCtx = cropCanvas.getContext('2d');

    if (!cropCtx) return;

    // Set output size (1:1 aspect ratio)
    const outputSize = 300;
    cropCanvas.width = outputSize;
    cropCanvas.height = outputSize;

    // Draw cropped image
    cropCtx.drawImage(
      this.image,
      this.cropArea.x,
      this.cropArea.y,
      this.cropArea.size,
      this.cropArea.size,
      0,
      0,
      outputSize,
      outputSize
    );

    // Convert to blob and emit
    cropCanvas.toBlob(
      blob => {
        if (blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            this.ref.close(reader.result);
          };
          reader.readAsDataURL(blob);
        }
      },
      'image/jpeg',
      0.9
    );
  }

  cancel(): void {
    this.ref.close();
  }
}
